// packages import
import { KeyboardArrowDown } from '@mui/icons-material'
import { Box, Button, Menu, MenuItem } from '@mui/material'
import { FC, MouseEvent, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// component import
import PreBillsFilters from '../../../components/billing/preBills/filters'
import { PreBillsListing } from '../../../components/billing/preBills/PreBillsListing'
import { ContentHead } from '../../../components/common/ContentHead'
import { EmptyData } from '../../../components/common/EmptyData'

// others
import { BillBulkAction, BillStatus, BulkActionResult, useBillBulkActionsMutation } from '../../../../generated'
import { Alert } from '../../../components/common/Alert'
import { AuthContext } from '../../../context'
import { printBills, printBillsV2 } from '../../../../services'
import { PreBillsContext } from '../../../context/PreBillsContext'
import { CardBox } from '../../../theme/styleComponents'
import { flexCenter } from '../../../theme/styleConstants'
import { ConfirmationModal } from '../../../components/common/ConfirmationModal'
import PreBillsBulkActions from './PreBillsBulkActions'
import GenerateCollectionBillsModal from './GenerateCollectionBillsModal'
import { hasAdminAccess, isDepartmentHead } from '../../../utils'
import { useDepartments } from '../../../hooks/useDepartments'
import { SelectedBills } from '../../../interfaceTypes'

export const PreBills: FC = (): JSX.Element => {
  const { preBills, loading, refetch } = useContext(PreBillsContext)
  const { isAttorney, isParalegal, isAccountant, currentUser } = useContext(AuthContext)
  const navigate = useNavigate()
  const { departments } = useDepartments({})
  const [bulkActionAnchorEl, setBulkActionAnchorEl] = useState<null | HTMLElement>(null)

  const [showVoidConfirmation, setShowVoidConfirmation] = useState(false)
  const [showApproveConfirmation, setShowApproveConfirmation] = useState(false)
  const [openModalToGenerateAllPreBills, setOpenModalToGenerateAllPreBills] = useState(false)
  const [openModalToGenerateCollectionBills, setOpenModalToGenerateCollectionBills] = useState(false)
  const [selectedPreBills, setSelectedPreBills] = useState<SelectedBills>({
    selectedIds: [],
    allSelected: false
  })

  const bulkActionOpen = Boolean(bulkActionAnchorEl)
  const isEmpty = preBills?.length === 0 && !loading
  const hasAdminPermissions = hasAdminAccess(currentUser)
  const canPerformBulkAction = hasAdminPermissions || isAttorney || isDepartmentHead(currentUser, departments)
  const selectedBillIds = selectedPreBills.selectedIds
  const allBillsSelected = selectedPreBills.allSelected

  const onCompletedBulk = (successMessage: string, results: BulkActionResult[]) => {
    const hasError = results.filter(result => !result.success)
    if (!hasError.length) {
      Alert.success(successMessage)
      refetch()
    } else
      hasError.forEach(failedBill => {
        Alert.error(`Bill ${failedBill.billId} failed due to ${failedBill.error}`)
      })
  }
  const [billBulkActions, { loading: performingAction }] = useBillBulkActionsMutation({
    onError: error => {
      console.log('Error in sending bills for approval', error)
      Alert.error(error.message)
    },
    onCompleted: data => {
      const { billBulkActions } = data || {}
      const { results, response } = billBulkActions || {}
      const { message } = response || {}
      onCompletedBulk(message || 'Done!', results)
    }
  })

  if (performingAction) Alert.info('Performing Action...')

  const handleBulkActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    setBulkActionAnchorEl(event.currentTarget)
  }

  const handleBulkActionClose = () => {
    setBulkActionAnchorEl(null)
  }

  const handleBulkPrint = async () => {
    const rawIds = allBillsSelected && preBills?.length ? preBills.map(b => b.id) : selectedBillIds
    const billIdsToPrint = rawIds?.map(id => (typeof id === 'number' ? id : Number(id))).filter(Number.isFinite) ?? []
    if (!billIdsToPrint.length) return
    try {
      await printBills(billIdsToPrint)
      handleBulkActionClose()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const handleBulkPrintV2 = async () => {
    const rawIds = allBillsSelected && preBills?.length ? preBills.map(b => b.id) : selectedBillIds
    const billIdsToPrint = rawIds?.map(id => (typeof id === 'number' ? id : Number(id))).filter(Number.isFinite) ?? []
    if (!billIdsToPrint.length) return
    try {
      await printBillsV2(billIdsToPrint)
      handleBulkActionClose()
    } catch (e) {
      Alert.error(e instanceof Error ? e.message : 'Failed to generate PDF')
    }
  }

  const handleNewPreBillClick = () => {
    navigate('/billing/generate-bills', { state: { type: 'attorney' } })
  }

  const handleBulkAction = async (action: BillBulkAction) => {
    const billIds = allBillsSelected && preBills?.length ? preBills.map(b => b.id) : selectedBillIds
    await billBulkActions({
      variables: {
        bulkInput: {
          billIds,
          bulkActionType: action
        }
      }
    })
    handleBulkActionClose()
    if (action === BillBulkAction.VoidBills) handleCloseVoidConfirmation()
    if (action === BillBulkAction.ApproveBills) handleCloseApproveConfirmation()
    setSelectedPreBills({ selectedIds: [], allSelected: false })
  }

  const handleCloseVoidConfirmation = () => setShowVoidConfirmation(false)
  const handleCloseApproveConfirmation = () => setShowApproveConfirmation(false)

  const checkToHideSendAttorneyApproval = () => {
    const commonBills = allBillsSelected ? preBills : preBills.filter(bill => selectedBillIds?.includes(bill.id))
    return !commonBills.find(
      preBill =>
        preBill.status === BillStatus.SentForApproval ||
        preBill.status === BillStatus.ApprovedByResponsibleAttorney ||
        preBill.status === BillStatus.SentToClient
    )
  }

  const canApproveBill = () => {
    if (hasAdminPermissions) {
      return true
    }

    const commonBills = allBillsSelected ? preBills : preBills.filter(bill => selectedBillIds?.includes(bill.id))

    if (
      commonBills.every(preBill => preBill.status === BillStatus.Draft || preBill.status === BillStatus.SentForApproval)
    ) {
      return commonBills.every(preBill => preBill.case.billing?.billApprover?.id === currentUser?.id)
    }

    if (commonBills.every(preBill => preBill.status === BillStatus.ApprovedByResponsibleAttorney)) {
      return commonBills.every(preBill => preBill.isDepartmentHead)
    }

    return false
  }

  const showAttorneyApprovalItem = hasAdminPermissions && checkToHideSendAttorneyApproval()
  const showApproveItem = canApproveBill()

  const renderActionButtons = () => (
    <Box display="flex" gap={2} alignItems="center">
      {canPerformBulkAction && (
        <Button
          variant="outlined"
          endIcon={<KeyboardArrowDown />}
          onClick={handleBulkActionClick}
          disabled={!selectedBillIds?.length && !allBillsSelected}
        >
          Bulk Action
        </Button>
      )}
      {canPerformBulkAction && (
        <Menu anchorEl={bulkActionAnchorEl} open={bulkActionOpen} onClose={handleBulkActionClose}>
          {/* <MenuItem onClick={handleBulkPrint}>Print</MenuItem> */}
          <MenuItem onClick={handleBulkPrintV2}>Print</MenuItem>
          {showAttorneyApprovalItem && (
            <MenuItem onClick={() => handleBulkAction(BillBulkAction.SendBillsToAttorney)}>
              Send for Attorney Approval
            </MenuItem>
          )}
          {showApproveItem && <MenuItem onClick={() => setShowApproveConfirmation(true)}>Approve</MenuItem>}
          {hasAdminPermissions && <MenuItem onClick={() => setShowVoidConfirmation(true)}>Void</MenuItem>}
        </Menu>
      )}

      {(hasAdminPermissions || isAccountant) && (
        <>
          <Button variant="outlined" onClick={() => setOpenModalToGenerateAllPreBills(true)}>
            Generate Pre Bills for All Attorneys
          </Button>
          <Button variant="outlined" onClick={() => setOpenModalToGenerateCollectionBills(true)}>
            Generate Collection Bills
          </Button>
        </>
      )}

      {(hasAdminPermissions || isAttorney || isParalegal) && (
        <Button variant="contained" color="primary" onClick={handleNewPreBillClick}>
          New Pre Bill
        </Button>
      )}
    </Box>
  )

  return (
    <CardBox marginTop={3}>
      <ContentHead title="Pre Bills" customButton={renderActionButtons} />

      <PreBillsFilters />

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title="No pre bills found" />
        </Box>
      ) : (
        <PreBillsListing
          preBills={preBills}
          selectedPreBills={selectedPreBills}
          setSelectedPreBills={setSelectedPreBills}
          loading={loading}
          refetch={refetch}
        />
      )}

      <PreBillsBulkActions
        openModalToGenerateAllPreBills={openModalToGenerateAllPreBills}
        setOpenModalToGenerateAllPreBills={setOpenModalToGenerateAllPreBills}
        refetch={refetch}
      />

      <GenerateCollectionBillsModal
        isOpen={openModalToGenerateCollectionBills}
        onClose={() => setOpenModalToGenerateCollectionBills(false)}
        refetch={refetch}
      />

      <ConfirmationModal
        isOpen={showVoidConfirmation}
        title="Void Bill"
        message={`Are you sure you want to void ${
          allBillsSelected ? 'all bills' : `bill with id(s) ${selectedBillIds.map(bill => `#${bill}`).join(', ')}`
        }? This action cannot be undone.`}
        confirmText="Void Bills"
        cancelText="Cancel"
        variant="danger"
        isLoading={performingAction}
        onConfirm={() => handleBulkAction(BillBulkAction.VoidBills)}
        onCancel={handleCloseVoidConfirmation}
      />

      <ConfirmationModal
        isOpen={showApproveConfirmation}
        title="Approve Bill(s)"
        message={`Are you sure you want to approve ${
          allBillsSelected ? 'all bills' : `bill with id(s) ${selectedBillIds.map(bill => `#${bill}`).join(', ')}`
        } ${hasAdminPermissions ? 'on behalf of Attorney/Dept Head' : ''}? This action cannot be undone.`}
        confirmText="Approve Bill(s)"
        cancelText="Cancel"
        variant="danger"
        isLoading={performingAction}
        onConfirm={() => handleBulkAction(BillBulkAction.ApproveBills)}
        onCancel={handleCloseApproveConfirmation}
      />
    </CardBox>
  )
}

export default PreBills
